<script>
  import { onMount, tick } from 'svelte';
  import Validators from '../../utils/validators';

  import { getList, getReport } from '../../api';
  import { reportDetailStore } from '../../stores/Reports';
  import { headerDisplayStore } from '../../stores/General';
  import Filter from '../../components/table/Filter.svelte';
  import { dateShort } from '../../utils/conv';
  import { Moon } from 'svelte-loading-spinners';
  import { clickOutside } from '../../utils/outsideClick';
  import CustomIcon from '../../components/CustomIcon.svelte';
  import ExportButton from '../../components/exportButton.svelte';
  const defaultFilter = {
    from_time: '01/01/2020',
    to_time: '01/01/2022',
  };

  export let onClose = () => {};
  export function reset() {
    form.reset();
  }

  let loading = false;
  let form;
  let rules = {
    title: {
      validators: [Validators.required, Validators.minLength(2)],
    },
    description: {
      validators: [Validators.required, Validators.minLength(4)],
    },
  };
  let data = [];
  let newBatch = [];
  let summary = {};
  let page = 0;
  let pageSize = 100;
  let sortBy = 'start_at';
  let sortOrder = 'ASC';

  // values sent to api
  let filter = {};
  // values sent to table filter
  let filters = [];
  let columns = [];

  function paramsToFilters(params = []) {
    const _filters = [];
    params.map((param) => {
      // prepare ui filter and set initial value from params
      const _filter = {
        label: param.displayName,
        key: param.paramName,
        filter: param.type,
        value: param.defaultValue || defaultFilter?.[param.paramName],
      };

      if (param.type == 'date') {
        _filter.filter = 'date_single';
      }
      if (param.type === 'dp_list') {
        _filter.getItems = fetchDemandPartners;
        _filter.filter = 'list';
      }
      if (param.type === 'iab_list') {
        _filter.getItems = fetchIabList;
        _filter.filter = 'list';
      }
      if (param.type === 'campaign_list') {
        _filter.getItems = fetchCampaigns;
        _filter.filter = 'list';
      }
      // TODO: add campaigns

      // update api filter with initial value from ui filter
      filter[param.paramName] = _filter.value;
      filter = filter;

      // dropdowns have object value
      // we only have initial value, use it as label too
      if (_filter.filter === 'list') {
        _filter.value = { value: _filter.value, label: _filter.value };
      }

      // add this to list of ui filters
      _filters.push(_filter);
    });
    return _filters;
  }

  async function loadData() {
    const report_id = $reportDetailStore.report_id || '';
    loading = true;
    try {
      const response = await getReport(pageSize, page, sortBy, sortOrder, report_id, filter);
      if (page == 0) {
        data = [];
      }
      newBatch = response.data.items;
      summary = response.data.summary || {};
    } catch (error) {
      headerDisplayStore.ShowError({
        title: 'Server error',
        subtitle: error,
      });
      onClose();
    } finally {
      loading = false;
    }
  }

  async function fetchDemandPartners(name = undefined) {
    try {
      const response = await getList('demand', 10, 1, 'name', 'ASC', { name });
      if (response?.data) {
        return response.data.map((item) => ({ value: item.demand_partner_id, label: item.name }));
      }
      return [];
    } catch (error) {
      console.warn(error);
      return [];
    }
  }

  async function fetchIabList(search = undefined) {
    try {
      const response = await getList('iab/category', 10, 1, 'name', 'ASC', { search });
      if (response?.data) {
        return response.data.map((item) => ({ value: item.iab_category_id, label: item.name }));
      }
      return [];
    } catch (error) {
      console.warn(error);
      return [];
    }
  }

  async function fetchCampaigns(search = undefined) {
    try {
      const response = await getList('campaign', 10, 1, 'name', 'ASC', { name: search });
      if (response?.data) {
        return response.data.map((item) => ({ value: item.campaign_id, label: item.name }));
      }
      return [];
    } catch (error) {
      console.warn(error);
      return [];
    }
  }

  function setWidth() {
    const viewport = document.getElementById('viewport');
    const reportContent = document.getElementById('report-content');
    if (reportContent) reportContent.style.maxWidth = viewport.clientWidth * 0.98 + 'px';
    const reportContainer = document.getElementById('report-container');
    if (reportContainer) reportContainer.style.marginRight = viewport.clientWidth * 0.01 + 'px';
  }

  async function onFilterChange(e) {
    const f = e.detail.filter;

    f.from_time = dateShort(f?.from_time) || undefined;
    f.to_time = dateShort(f?.to_time) || undefined;

    page = 0;
    filter = Object.assign({}, filter, f);
    await tick();
    if (page === 0 && loading) return;
    loadData();
  }

  function setColumns() {
    columns = data.length > 0 ? Object.keys(data[0]) : [];
  }

  onMount(async () => {
    setWidth();
    const reportType = $reportDetailStore;
    const _filters = paramsToFilters(reportType.params);
    filters = _filters;

    // fetch for types without filters
    if (filters.length === 0) {
      await loadData();
    }
  });

  $: data = [...data, ...newBatch];

  $: if (data) {
    setColumns();
  }
</script>

<div
  class="flex justify-center items-center fixed z-100 inset-0 overflow-y-auto"
  aria-labelledby="modal-title"
  role="dialog"
  aria-modal="true"
>
  <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" />
  <div
    class="flex items-center justify-start h-screen text-right !sm:block sm:p-0"
    id="report-container"
  >
    <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true"
      >&#8203;</span
    >

    <!-- white modal -->
    <div
      class="inline-block bg-white rounded-lg px-6 pt-5 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full sm:w-full sm:p-6"
      style="margin-top: 10px;margin-bottom: 10px;max-height: calc(100vh - 20px);max-width: 98vw;min-height:60vh;min-width:50vw"
      id="report-content"
      use:clickOutside
      on:click_outside={onClose}
    >
      <div class="flex flex-col justify-between" style="max-height: calc(100vh - 50px);">
        <!-- close icon -->
        <div class="block absolute top-8 right-5 z-40 ">
          <div class="flex flex-row w-16 justify-end">
            <div>
              <div
                type="button"
                class="hover:text-gray-500 border-transparent cursor-pointer"
                on:click={onClose}
              >
                <span class="sr-only">Close</span>

                <CustomIcon width="18" height="18" name="close_dialog" />
              </div>
            </div>
          </div>
        </div>
        <div class="block">
          <div class="flex flex-row w-16 justify-start">
            <ExportButton name={$reportDetailStore.title} {data} {summary} disabled={loading} />
          </div>
        </div>

        <!-- modal title -->
        <div class="text-2xl font-bold mb-2 h-15 flex-initial text-center">
          {$reportDetailStore.title}
        </div>
        <div>
          <Filter {filters} on:filterChange={onFilterChange} />
        </div>

        <div class="overflow-y-scroll flex flex-col">
          <div class="overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div class="py-2 inline-block min-w-full sm:px-6 lg:px-8">
              <div class="">
                <table class="min-w-full">
                  {#if loading}
                    <div class="flex justify-center items-center cell-content pl-6 pr-6 pt-40 pb-8">
                      <Moon size="20" color="#FF3E00" unit="px" duration="1s" />
                    </div>
                  {/if}
                  <thead class="sticky top-0">
                    <tr class="top-0 h-16 py-8 bg-yellow-100">
                      {#each columns as col}
                        <th
                          scope="col"
                          class="text-sm font-medium text-gray-900 px-6 py-4 text-left"
                        >
                          {col}
                        </th>
                      {/each}
                    </tr>
                  </thead>
                  <tbody>
                    {#each data as row, i}
                      <tr class="border-b">
                        {#each Object.keys(row) as column}
                          <td class="text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap">
                            {row[column] || ''}
                          </td>
                        {/each}
                      </tr>
                    {/each}
                    <!-- summary -->
                  </tbody>
                  {#if !!summary && Object.keys(summary).length > 0}
                    <tfoot class="sticky bottom-0 bg-white">
                      <tr class="border-b">
                        {#each columns as column, index}
                          <td class="text-sm text-gray-900 font-bold px-6 py-4 whitespace-nowrap">
                            {summary[column] || (index === 0 ? !!summary[column] || 'Summary' : '')}
                          </td>
                        {/each}
                      </tr>
                    </tfoot>
                  {/if}
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
